
import {Component, Mixins, Prop} from 'vue-property-decorator';
import {Doughnut} from 'vue-chartjs';
import {ChartConfig} from '@/features/reports/components/chartjs/chartConfig';
import {Data, Series} from '@/features/reports/models';
import Chart, {ChartData, ChartDataSets, ChartLegendItem, ChartTooltipModel} from 'chart.js';
import ColourService from '@/shared/services/color';
import {
	DoughnutChartLabel,
	DoughnutLabelConfig,
	LabelProvider,
	ResizeStrategy
} from '@/features/reports/components/chartjs/plugins/doughnutLabels/types';
import { chartDataUtil } from '../../util/ChartDataUtil';
import { LabelsAndData } from '../../models/labelsAndData';
import HtmlFormatter from '@/shared/services/htmlFormatter';

@Component
export default class DoughnutChartBaseWrapper extends Mixins(Doughnut) {
	@Prop() public title: string;
	@Prop() public data: Data;
	@Prop() public labelsAndData: LabelsAndData; // for flows compatibility
	@Prop() public legendElementId: string;

	private chartData: ChartData;

	public async mounted() {
		this.chartData = this.mapData();
		const options = this.overrideOptions(ChartConfig.globalOptions);

		this.renderChart(this.chartData, options);
		this.createLegend();
	}

	public overrideOptions(options: Chart.ChartOptions): Chart.ChartOptions {
		options.cutoutPercentage = 73;
		options.plugins!.inlineLabels = false;
		options.plugins!.crosshair = false;
		options.tooltips = {
			enabled: false
		};
		options.plugins!.doughnutlabel = {
			labelProvider: this.labelProvider(),
			resizeStrategy: ResizeStrategy.TRUNCATE
		};
		options.legendCallback = (chart: Chart) => {
			const chartId: number = (chart as any).id;

			const legendItems: ChartLegendItem[] = (chart as any).legend.legendItems;
			const dataSets: ChartDataSets = chart.data.datasets![0];
			const backgroundColours: string[] = dataSets.backgroundColor as string[];

			const text = [];
			text.push(`<table class="chartjs-custom-legend"><colgroup><col/><col width="100%"/><col width="0%"/></colgroup>`);

			for (let i = 0; i < legendItems.length; i++) {
				if (legendItems[i].text) {
					const originalLabel = legendItems[i].text || '';
					const encodedLabel = HtmlFormatter.escapeHtmlCharacters(originalLabel);
					const legendColour = i >= backgroundColours.length ? '#e5e5e5' : backgroundColours[i];
					text.push(`<tr>`);
					text.push(`<td>`);
					text.push(`<div style="width: 8px; background-color:${legendColour}">&nbsp;</div>`);
					text.push('</td>');
					text.push(`<td class="legend-label" title="${encodedLabel}">${encodedLabel}</td>`);
					text.push(`<td class="legend-value">${dataSets.data![i]}</td>`);
					text.push('</tr>');
				}
			}
			text.push('</table>');
			return text.join('');
		};
		options.legend!.display = false;
		return options;
	}

	public mapData(): ChartData {
		let labels: string[] = [];
		let data: number[] = [];

		if (this.data) {
			labels = this.data.scale.labels;
			data = this.data.series[0].data; // only support a single series
		} else if (this.labelsAndData) {
			labels = this.labelsAndData.labels;
			data = this.labelsAndData.data;
		}

		// assumes that any grouping into "others" and ordering has already been done
		const coloursRequired = labels.length;
		const backgroundPalette = ChartConfig.backgroundColoursForRandomItems(coloursRequired);
		const borderPalette = ChartConfig.singleColourPalette(coloursRequired, '#ffffff');

		return {
			labels,
			datasets: [
				{
					data,
					backgroundColor: backgroundPalette,
					hoverBackgroundColor: ColourService.setAlphas(backgroundPalette, 0.6),
					borderColor: borderPalette,
					hoverBorderColor: borderPalette,
					borderWidth: 3,
				}
			]
		};
	}

	private getHoverTooltipWhenNoSelection(): DoughnutChartLabel[] {
		return [
			{ text: String(this.total), font: { size: 40, family: '"Quicksand", sans-serif' } },
			{ text: this.title, font: { size: 12, family: '"Quicksand", sans-serif' } },
		];
	}

	private labelProvider(): LabelProvider {
		return (chart, index, key, value) => {
			if (value === undefined || !key) {
				return this.getHoverTooltipWhenNoSelection();
			} else {
				return this.getHoverTooltipWhenSelection(String(key), value);
			}
		};
	}

	private getHoverTooltipWhenSelection(legendItemName: string, value: number): DoughnutChartLabel[] {
		const total = this.total;
		const percentageOfTotal = (value / total) * 100;
		const roundedPercentage = Math.round(percentageOfTotal * 100) / 100;

		return [
			{ text: `${roundedPercentage}%`, font: { size: 40, family: '"Quicksand", sans-serif' } },
			{ text: legendItemName, font: { size: 12, family: '"Quicksand", sans-serif' } },
		];
	}

	private get total(): number {
		return chartDataUtil.total(this.chartData.datasets![0].data as number[]);
	}

	private createLegend() {
		if (this.$data._chart) {
			const chart: Chart = this.$data._chart;

			const legendContainer = document.getElementById(this.legendElementId);
			legendContainer!.innerHTML = chart.generateLegend().toString();

			if (this.total > 0) {
				const legendRows = legendContainer!.getElementsByTagName('tr');
				let index = 0;
				for (const row of legendRows) {
					const hoveredDatasetIndex = index++;
					row.addEventListener('mouseover', (event) => {
						row.style.backgroundColor = '#e5e5e5';
						this.highlightActiveSegment(hoveredDatasetIndex, true);
						chart.options.tooltips!.custom!(this.createTooltipEventObject(chart, hoveredDatasetIndex));
					});
					row.addEventListener('mouseout', (event) => {
						row.style.backgroundColor = '';
						this.highlightActiveSegment(hoveredDatasetIndex, false);
						const tooltipEvent: any = { opacity: 0 };
						chart.options.tooltips!.custom!(tooltipEvent as ChartTooltipModel);
					});
				}
			}
		}
	}

	private createTooltipEventObject(chart: Chart, hoveredDatasetIndex: number): ChartTooltipModel {
		const legendItems: ChartLegendItem[] = (chart as any).legend.legendItems;
		const dataSets: ChartDataSets = chart.data.datasets![0];
		return {
			opacity: 1,
			body: [
				{
					lines: [
						legendItems[hoveredDatasetIndex].text + ': ' + String(dataSets.data![hoveredDatasetIndex])
					]
				}
			],
			dataPoints: [
				{index: hoveredDatasetIndex}
			]
		} as ChartTooltipModel;
	}

	private highlightActiveSegment(segmentIndex: number, highlight: boolean) {
		const chart: Chart = this.$data._chart;
		const metaData = chart.getDatasetMeta(0);
		const activeSegment = metaData.data[segmentIndex];
		(chart as any).updateHoverStyle([activeSegment], null, highlight);
		chart.render();
	}
}
